// MoveActionDialog — move an action to a different action category.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { actionsApi } from '@/lib/api/actions'
import { categoriesApi, type Category } from '@/lib/api/categories'
import { queryKeys } from '@/lib/api/queryKeys'
import { HierarchyPickerDialog, type HierarchyNode } from '@/components/shared/HierarchyPickerDialog'

interface MoveActionDialogProps {
  open: boolean
  onClose: () => void
  onMoved: () => void
  actionId: string
  currentCategoryId: string
  currentCategoryName: string
}

export function MoveActionDialog({
  open,
  onClose,
  onMoved,
  actionId,
  currentCategoryId,
  currentCategoryName,
}: MoveActionDialogProps) {
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const actApi = actionsApi(api)
  const catApi = categoriesApi(api, 'action')

  const { data: categories } = useQuery({
    queryKey: queryKeys.categories.tree(slug, 'action'),
    queryFn: () => catApi.tree(),
    enabled: open,
  })

  const moveMutation = useMutation({
    mutationFn: ({ destId, notes }: { destId: string; notes: string }) =>
      actApi.move(actionId, destId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.actions.detail(slug, actionId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.tree(slug, 'action') })
      queryClient.invalidateQueries({ queryKey: queryKeys.actions.all(slug) })
      onMoved()
    },
  })

  const nodes: HierarchyNode[] = (categories ?? []).map((c: Category) => ({
    id: c.id,
    name: c.name,
    parent_id: c.parent_id,
  }))

  function handleConfirm(selectedId: string, changeNotes: string) {
    moveMutation.mutate({ destId: selectedId, notes: changeNotes })
  }

  return (
    <HierarchyPickerDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      title="Move Action"
      subtitle={`Current category: ${currentCategoryName}`}
      nodeLabel="category"
      nodes={nodes}
      currentId={currentCategoryId}
      isPending={moveMutation.isPending}
      error={moveMutation.error ? (moveMutation.error as Error).message : null}
    />
  )
}
